import { Radio } from '@remote-com/mushu/src/components/Ui/Form/Radio/Radio';
import {
  Radio as RadioWrapper,
  Label as RadioLabel,
  Description as RadioDescription,
  RadioState,
} from '@remote-com/mushu/src/components/Ui/Form/Radio/Radio.styled';
import IconCheck from '@remote-com/norma/icons/check.svg';
import { Formik } from 'formik';
import { useState } from 'react';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { breakpointFrom } from 'remote-com-shared/style/mediaQueries';
import styled, { css } from 'styled-components';

import { RichTextBaseStyle } from '@/src/components/shared/RichText';

export const GET_STARTED_URL = `${process.env.NEXT_PUBLIC_EMPLOY_BASE_URL}/sign-up`;
export const GET_QUOTE_URL = `${process.env.NEXT_PUBLIC_PANDA_BASE_URL}/contact-us`;

export const Section = styled.section`
  ${({ $tone }) => {
    switch ($tone) {
      case 'light':
        return css`
          background-image: none;
          background-color: var(--colors-blank);
        `;
      default:
        return css`
          background-image: var(--colors-zircon);
        `;
    }
  }}
`;

const breakpointReference = breakpointFrom.lg;

export const PricingTable = styled.div`
  --cellSpacingX: 22px;
  --cellSpacingY: 16px;
  --edgeSpacing: 50px;
  --cardSpacingX: 24px;
  --cardSpacingY: 32px;
  --intervalSelectorSpacing: 25px;
  --buttonColor: #fff;
  --buttonBackground: var(--colors-irisBlue);

  display: flex;
  flex-direction: column;

  ${breakpointReference(css`
    display: grid;
    grid-template-columns:
      repeat(3, [column] 1fr)
      [end];
    /* grid-template-columns: [features] 1.65fr [contractors] 1fr [peo] 1fr [eor] 1fr [eorplus] 1fr [end]; */
    grid-template-rows:
      [label] auto [cost] auto [controls] auto [intro] auto repeat(4, [feature] auto)
      [cta] auto [footer] auto [end];
    grid-auto-flow: column;
  `)}
`;

export const BaseCell = styled.div`
  ${fluidProp(['padding-left', 'padding-right'], '12px', '36px')}

  a {
    color: inherit;
  }
`;

export const TitleCell = styled(BaseCell)`
  position: relative;
  ${({ theme }) => theme.typography.bodyBold}
  text-transform: uppercase;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.05em;
  ${fluidProp(['padding-top'], '20px', '56px')}
  padding-bottom: 26px;
`;

export const ColumnHighlightMessage = styled.div`
  text-transform: none;
  ${({ theme }) => theme.typography.bodyCaption}
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  color: var(--colors-darkBlue);
  background-color: var(--colors-turquoise);
  padding-top: 16px;
  padding-bottom: 16px;

  strong {
    font-weight: 600;
  }

  > * {
    margin: 0;
  }
`;

export const PriceCell = styled(BaseCell)`
  text-align: center;
  padding-bottom: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const BillingFrequencyCell = styled(BaseCell)`
  padding-top: 16px;
`;

export const DescriptionCell = styled(BaseCell)`
  text-align: center;
  border-bottom: 1px solid var(--borderColor);
  padding-top: 16px;
  padding-bottom: 16px;
`;

export const FeatureCell = styled(BaseCell)`
  padding-top: 10px;
  padding-bottom: 10px;

  > *:first-child {
    margin-top: 0;
  }
  > *:last-child {
    margin-bottom: 0;
  }

  ${DescriptionCell} + & {
    padding-top: 32px;
  }
`;

export const FeatureCellContent = styled.div`
  position: relative;
  padding-left: calc(16px + 8px);
`;

export const FeatureCellIcon = styled(IconCheck)`
  position: absolute;
  width: 16px;
  color: var(--colors-turquoise);
  top: 4px;
  left: 0;
`;

export const CTACell = styled(BaseCell)`
  ${({ theme }) => theme.typography.bodyBold}
  text-transform: uppercase;
  text-align: center;
  padding-top: 32px;
  padding-bottom: 16px;
`;

export const FooterCell = styled(BaseCell)`
  ${RichTextBaseStyle}
  --textHighlightSecondary: var(--colors-turquoise);
  text-align: center;
  padding-top: 16px;
  padding-bottom: 32px;
`;

export const Column = styled.div`
  ${breakpointReference(css`
    display: contents;
  `)}
  --borderColor: #d4d7df;

  ${({ $highlight }) =>
    $highlight &&
    css`
      order: -1;

      ${BaseCell} {
        --borderColor: #7764e8;
        color: var(--colors-selago);
        background-color: var(--colors-irisBlue);
        background-image: linear-gradient(270deg, rgb(89, 65, 215), rgb(73, 55, 177));
      }

      ${TitleCell} {
        color: var(--colors-blank);
      }
    `};
`;

export const SimplePriceText = styled.span`
  ${({ theme }) => theme.typography.titleXXL}
  font-weight: 600;
  color: var(--colors-irisBlue);
`;

export const ScreamPriceText = styled(SimplePriceText)`
  && {
    ${({ theme }) => theme.typography.scale4}
    text-transform: uppercase;
    position: relative;

    ${({ $crossed }) =>
      $crossed &&
      css`
        ${({ theme }) => theme.typography.titleLG}
        font-weight: 600;

        &:first-letter {
          ${({ theme }) => theme.typography.bodyLeadBold}
        }

        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: -8%;
          right: 5%;
          width: 53px;
          height: 3px;
          background-color: var(--colors-irisBlue);
          opacity: 0.6;
          transform: rotate(-12.53deg);
          transform-origin: center;
        }
      `}
  }
`;

export const ContractorPriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const SpecialPriceWrapper = styled.div``;

export const SpecialPriceStart = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  color: var(--colors-spindle);
`;

export const SpecialPriceBadge = styled.div`
  ${({ theme }) => theme.typography.bodySMMedium}
  text-transform: uppercase;
  color: var(--colors-turquoise);
  padding: 10px 0 0 5px;
`;

export const SpecialPriceValue = styled.div`
  position: relative;
  ${({ theme }) => theme.typography.scale4}

  ${({ $crossed }) =>
    $crossed &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 46%;
        left: -7%;
        right: 5%;
        height: 3px;
        background-color: var(--colors-turquoise);
        transform: rotate(-12.53deg);
        transform-origin: center;
      }
    `}
`;

export const SpecialPriceDescription = styled.div``;

export const BillingFrequencyControls = styled.div`
  display: flex;
  justify-content: space-evenly;

  ${RadioWrapper} {
    flex-direction: row;
    align-items: flex-start;
    margin-top: 0;
  }

  ${RadioLabel},
  ${RadioDescription} {
    margin-left: 10px;
  }

  ${RadioDescription} {
    color: var(--colors-spindle);
  }

  ${RadioState} {
    border-color: rgba(208, 202, 247, 0.4);

    &:hover {
      background-color: transparent;
      border-color: rgba(208, 202, 247, 0.8);
    }
  }

  input:checked ~ ${RadioState} {
    border-color: rgba(183, 184, 235, 0.6);
    &::before {
      background-color: var(--colors-turquoise);
    }
    &:hover {
      background-color: transparent;
    }
  }
`;

export const InternationalPricingButton = styled.button`
  font-size: 0.9em;
  font-weight: inherit;
  background: transparent;
  padding: 0;
  border: none;

  position: relative;
  color: var(--textHighlightColor);

  &::after {
    content: '';
    position: absolute;
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-bottom-color: var(--textHighlightSecondary);
    opacity: var(--textHighlightSecondaryOpacity);
    bottom: -5px;
    left: 0;
    right: 0;
    pointer-events: none;
  }

  &:hover,
  &:focus {
    &::after {
      border-bottom-width: 2px;
    }
  }
`;

export const CTAButton = styled(Button)`
  text-transform: none;
`;

export function PlanColumn({
  $highlight,
  title,
  price,
  billingFrequency,
  description,
  features,
  cta,
  footer,
}) {
  return (
    <Column $highlight={$highlight}>
      <TitleCell>{title}</TitleCell>
      <PriceCell>{price}</PriceCell>
      <BillingFrequencyCell>{billingFrequency}</BillingFrequencyCell>
      <DescriptionCell>{description}</DescriptionCell>
      {features.map((feature, index) => (
        <FeatureCell key={index}>
          <FeatureCellContent>
            <FeatureCellIcon />
            <p>{feature}</p>
          </FeatureCellContent>
        </FeatureCell>
      ))}
      <CTACell>{cta}</CTACell>
      <FooterCell>{footer}</FooterCell>
    </Column>
  );
}

function costByFrequency(frequency) {
  if (frequency === 'monthly') {
    return '$349';
  }
  return '$299';
}

function GlobalEmploymentColumn({
  onInternationalCurrencyClick,
  hideOffer,
  hideAnnualSavingsLabel,
  CustomCTAButton,
}) {
  const [billingFrequency, setBillingFrequency] = useState('annually');
  return (
    <PlanColumn
      $highlight
      title={
        <>
          Employer of Record
          {!hideOffer && (
            <ColumnHighlightMessage id="free-price-description" aria-hidden="true">
              * Sign up now to get your <strong>1st employee FREE</strong>
            </ColumnHighlightMessage>
          )}
        </>
      }
      price={
        <SpecialPriceWrapper>
          <SpecialPriceStart>Starts at</SpecialPriceStart>
          <SpecialPriceBadge aria-describedby="free-price-description">Free*</SpecialPriceBadge>
          <SpecialPriceValue $crossed>{costByFrequency(billingFrequency)}</SpecialPriceValue>
          <SpecialPriceDescription>employee/month</SpecialPriceDescription>
        </SpecialPriceWrapper>
      }
      billingFrequency={
        <BillingFrequencyControls>
          <Formik
            initialValues={{
              billingFrequency: 'annually',
            }}
          >
            {({ handleChange }) => (
              <>
                <Radio
                  name="billingFrequency"
                  value="annually"
                  label="Annually"
                  description={hideAnnualSavingsLabel ? undefined : '(Save 14%)'}
                  onChange={(event) => {
                    handleChange(event);
                    setBillingFrequency(event.target.value);
                  }}
                />
                <Radio
                  name="billingFrequency"
                  value="monthly"
                  label="Monthly"
                  description=""
                  onChange={(event) => {
                    handleChange(event);
                    setBillingFrequency(event.target.value);
                  }}
                />
              </>
            )}
          </Formik>
        </BillingFrequencyControls>
      }
      description={<p>We handle employment in countries where you don&apos;t have entities</p>}
      features={[
        'Onboard, pay, and manage contractors and employees in one secure platform',
        'Competitive benefits packages tailored to each country',
        'Get support from local HR and legal experts, no third-party delays',
        'Ensure your IP and invention rights are fully protected',
      ]}
      cta={
        CustomCTAButton ? (
          <CustomCTAButton>Get started</CustomCTAButton>
        ) : (
          <CTAButton tone="primary" shadow href={GET_STARTED_URL} rel="nofollow">
            Get started
          </CTAButton>
        )
      }
      footer={
        <p>
          <InternationalPricingButton onClick={onInternationalCurrencyClick}>
            Looking for pricing in your currency?
          </InternationalPricingButton>
        </p>
      }
    />
  );
}

export function PricingOverviewSection({
  onInternationalCurrencyClick,
  tone,
  hideOffer = false,
  hideAnnualSavingsLabel = false,
  CTAButton: CustomCTAButton,
}) {
  return (
    <Section $tone={tone} id="pricing">
      <ContentContainer>
        <PricingTable>
          <PlanColumn
            title={
              <>
                Contractor <br /> management
              </>
            }
            price={<ScreamPriceText>Free</ScreamPriceText>}
            description={<p>Manage and pay international contractors</p>}
            features={[
              'Onboard contractors quickly and safely with localized contracts',
              'Pay contractors on time in their local currency with zero exchange fees',
              'Manage all employment documents in our secure platform',
              'Automatically generate 1099s for US contractors',
            ]}
            cta={
              CustomCTAButton ? (
                <CustomCTAButton>Get started</CustomCTAButton>
              ) : (
                <CTAButton tone="primary" shadow href={GET_STARTED_URL} rel="nofollow">
                  Get started
                </CTAButton>
              )
            }
          />
          <GlobalEmploymentColumn
            onInternationalCurrencyClick={onInternationalCurrencyClick}
            hideOffer={hideOffer}
            hideAnnualSavingsLabel={hideAnnualSavingsLabel}
            CustomCTAButton={CustomCTAButton}
          />
          <PlanColumn
            title={
              <>
                Global payroll <br /> &amp; benefits
              </>
            }
            price={<SimplePriceText>Get a quote</SimplePriceText>}
            description={
              <p>Multi-country payroll and benefits in countries where you have entities</p>
            }
            features={[
              'Consolidate payroll processing across multiple countries',
              'Ensure compliance with ever-changing local payroll regulations',
              'Access payroll reports by country',
              'Reduce costs by centralizing payroll management',
            ]}
            cta={
              CustomCTAButton ? (
                <CustomCTAButton>Get started</CustomCTAButton>
              ) : (
                <CTAButton tone="primary" shadow href={GET_QUOTE_URL}>
                  Get a quote
                </CTAButton>
              )
            }
          />
        </PricingTable>
      </ContentContainer>
    </Section>
  );
}
